import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReactFlow, type Node, type NodeTypes } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import './Map3ImplementationPlans.css';
import type { ImplementationPlanColumn } from '@mm-shared/types/Objective1';
import { PlanColumnNode } from './components/PlanColumnNode';
import { ObjectiveStickyNoteNode } from './components/ObjectiveStickyNoteNode';
import { computeLayout, projectView, collapseKey, hideDoneKey } from './utils/planLayout';
import type { HideDone, PlanCardCollapse } from './utils/planLayout';
import { cardStatusLabel, isCardDone } from './utils/cardStatus';
import {
  usePlanNodesChangeHandler,
  resolveKeyboardMove,
  type MoveCardHandler,
  type KeyboardMoveAction,
} from './hooks/usePlanNodesChangeHandler';
import {
  restoreFocus,
  focusCardNode,
  decideFocusRestore,
  type PendingFocus,
} from './utils/focusRestore';
import type { PlanCard } from './types';

const nodeTypes: NodeTypes = {
  planColumn: PlanColumnNode,
  stickyNote: ObjectiveStickyNoteNode,
};

export type Map3ImplementationPlansProps = {
  cards: PlanCard[];
  /**
   * Rebuild trigger (ADR §2 "Reconciliation with remote echoes"): local node
   * state is rebuilt from props only when this key changes, so a remote echo
   * that matches local state is a no-op and dropped cards do not bounce.
   */
  layoutKey: string;
  canEdit: boolean;
  /** Persists one card move (ADR §1e write shape). Exactly one call per drop. */
  onMoveCard: MoveCardHandler;
  /** Persists a title edit (ADR §5). */
  onEditTitle: (objectiveUuid: string, text: string) => void;
  /** Persists a new key result (ADR §5). */
  onAddKeyResult: (objectiveUuid: string, text: string) => void;
  /** Persists a key-result text edit (ADR A3). */
  onEditKeyResult: (objectiveUuid: string, resultUuid: string, text: string) => void;
  /**
   * Persists a key-result delivered toggle (ADR.ImplementationPlansUX2 C2b).
   * Returns whether it actually dispatched — the one write handler here whose
   * return value is load-bearing: the canvas reads it to decide whether the
   * toggle that just fired is safe to chase with a pending-focus record
   * (C6b), since a declined write cannot be the reason a card disappears.
   */
  onToggleKeyResultDelivered: (
    objectiveUuid: string,
    resultUuid: string,
    delivered: boolean,
  ) => boolean;
  /** Persists an additionalInfo edit from the details dialog (ADR A4c). */
  onEditAdditionalInfo: (objectiveUuid: string, text: string) => void;
};

/**
 * Every column showing everything: the canvas's initial hide-done state
 * (ADR.ImplementationPlansUX2 C4a/C4b). Module-level and never mutated, so
 * every instance can share the object; the canvas's own `setHideDone` is the
 * only thing that ever produces a different one.
 */
const HIDE_DONE_OFF: HideDone = { now: false, next: false, someday: false };

/**
 * Map3ImplementationPlans — the ReactFlow sticky-note board (ADR §2).
 *
 * Presentational: zero Redux imports; all data and handlers arrive via props
 * from Map3ImplementationPlansWrapper.
 *
 * Drag follows commit-on-drop (ADR §2 event table) via usePlanNodesChangeHandler:
 * local state during drag, exactly one onMoveCard on drop. Cards are draggable
 * only when canEdit. Title editing, the "+" add-key-result input and the
 * in-place key-result row editor live on the card (ADR §5, A3) and dispatch
 * through onEditTitle / onAddKeyResult / onEditKeyResult; keyboard moves land
 * in mr-kv9w.7.
 *
 * Canvas configuration per the ADR §2 table: zoom pinned to 1 so flow coordinates
 * equal screen coordinates; the page scrolls, not the canvas; multi-select is
 * disabled (selectionKeyCode/multiSelectionKeyCode null — the load-bearing props
 * for the one-write-per-drag invariant); cards are Tab-reachable but ReactFlow's
 * built-in arrow-key pixel-nudging is off (disableKeyboardA11y). Canvas and node
 * dimensions are computed by planLayout, never measured: the board mounts inside
 * a hidden named-view sibling where measurement reads 0x0.
 */
export function Map3ImplementationPlans({
  cards,
  layoutKey,
  canEdit,
  onMoveCard,
  onEditTitle,
  onAddKeyResult,
  onEditKeyResult,
  onToggleKeyResultDelivered,
  onEditAdditionalInfo,
}: Map3ImplementationPlansProps) {
  /**
   * Collapse state (ADR B1): the uuids that are currently `titleOnly`, owned by
   * this component in plain `useState` — the `Map3Relatives.tsx:210` pattern,
   * and `REDUX.md:469`'s default. Nothing about collapse enters `map3UISlice`,
   * `map3DataSlice`, `map3PendingSlice`, `PlanCard`, `deriveCards` or the
   * wrapper: no other component reads it, and it must NOT survive a remount
   * (B9 — every card starts expanded on a reload, a new tab or a fresh
   * navigation).
   *
   * A `Set` of collapsed uuids rather than a `Record<uuid, level>`: with exactly
   * two states, membership IS the state, and a card that arrives from a
   * collaborator after a collapse-all is absent and therefore expanded, which is
   * the correct default. The set is never pruned — a stale entry for a deleted
   * objective is a member nothing looks up (B1's accepted caveat).
   *
   * The setter is `handleSetCollapse` below, wired through node data as
   * `onSetCollapse` (ADR B4a) — the first user-facing entry point into this
   * state (mr-qmas.2 shipped the model with none).
   */
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set());

  /**
   * The board's SECOND session-only view state (ADR.ImplementationPlansUX2
   * C4a/C4b): one hide-done boolean per column, owned by this component in
   * plain `useState` for the same reasons `collapsed` is — nothing remounts on
   * any navigation a user performs, so `REDUX.md`'s "must persist across
   * remounts" trigger never fires, and the default is component state. The
   * setter is `handleToggleHideDone` below, wired through node data as
   * `onToggleHideDone` (C4c) — the column header's third button, in the same
   * `gap-1` cluster as expand-all/collapse-all, no divider. Both this and
   * `collapsed` are reset together by the wrapper's `key={mapUuid}` (C4b),
   * which closes the cross-map leak fact 11/12 identify.
   */
  const [hideDone, setHideDone] = useState<HideDone>(HIDE_DONE_OFF);

  /**
   * The hidden projection, frozen for the duration of a drag (C6c). Created
   * here because the canvas is what both captures it (through the change
   * handler) and releases it (in `handleDropSettled` and in the rebuild
   * effect's aborted-drag escape) — revision 2's lift clause was phrased on
   * `draggedIdRef`, which the handler nulls BEFORE calling `onDropSettled`,
   * leaving nothing to test by the time the callback ran.
   */
  const hiddenFreezeRef = useRef<ReadonlySet<string> | null>(null);

  // Bumped when a rebuild was suppressed mid-drag, so the deferred remote echo
  // is applied once the drop settles (ADR §2 reconciliation) — and, since C6c,
  // whenever a freeze was in force, so the TRUE projection returns after the
  // drop. Declared here rather than beside the rebuild effect because
  // `viewCards` reads it.
  const [rebuildTick, setRebuildTick] = useState(0);
  const pendingRebuildRef = useRef(false);

  /**
   * Memoized, because everything downstream is identity-sensitive (ADR B1):
   * called inline this would return a fresh array every render, defeating the
   * `layout` memo below and churning the `useCallback` identity of
   * `onNodesChange`, whose dependency list starts with `cards`. The rebuild
   * effect is protected by its own string keys (B3), which is exactly why this
   * would have been a silent per-frame cost rather than a visible bug.
   *
   * **One projection point** (C5a): collapse and visibility are applied here
   * together, so no path exists by which a view-modified card reaches the
   * renderer but not the drop resolver.
   *
   * Reading `hiddenFreezeRef` inside the memo body is deliberate. The freeze
   * only needs to be *in force* on recomputes that happen mid-drag — every one
   * of which is driven by `cards`, and by then the ref is set — and to *lift*
   * after the drop, which `rebuildTick` guarantees (C6c). `hideDone` is read
   * through its string key for the same reason `collapsed` is: a `Record`
   * rebuilt per render has no stable primitive identity.
   */
  const hideDoneKeyValue = useMemo(() => hideDoneKey(hideDone), [hideDone]);
  const viewCards = useMemo(
    () => projectView(cards, collapsed, hideDone, hiddenFreezeRef.current ?? undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cards, collapsed, hideDoneKeyValue, rebuildTick],
  );
  /**
   * The **view** rebuild key (ADR B3). Two keys with two owners: `layoutKey` is
   * the data key and belongs to the Redux boundary that derives the data; this
   * one is a view key and belongs to the component that owns the view state.
   * Folding collapse into `cardLayoutKey` would require the wrapper to know the
   * canvas's UI state, which is B1 inverted. `hideDoneKeyValue` above is its
   * sibling under the same rule (C6a) — both view keys are canvas-owned.
   */
  const collapseKeyValue = useMemo(() => collapseKey(collapsed), [collapsed]);

  const layout = useMemo(() => computeLayout(viewCards), [viewCards]);

  const [nodes, setNodes] = useState<Node[]>([]);
  /**
   * The exact array reference the rebuild effect last passed to `setNodes`
   * (ADR.ImplementationPlansUX2 C6b, tech-lead ruling on PR #482's remedy).
   * The restore effect below declines to decide focus on any commit whose
   * `nodes` is not this one. `applyNodeChanges` (ReactFlow's own
   * select/position echo, applied via `usePlanNodesChangeHandler`) always
   * returns a FRESH array, so it can never collide with this reference —
   * a commit driven by anything other than this component's own rebuild is
   * therefore unambiguously "not the rebuild's DOM" and must not be read.
   */
  const lastBuiltNodesRef = useRef<Node[] | null>(null);

  /**
   * Keyboard move (ADR §6): resolves through resolveKeyboardMove and dispatches
   * through the SAME onMoveCard as the pointer path — one write, §1e shape.
   * A noop/error resolution dispatches nothing (and leaves focus untouched —
   * no rebuild follows).
   *
   * Handlers reach node data through refs with a stable wrapper identity, so
   * the rebuild effect's deps stay [layoutKey, collapseKeyValue, canEdit,
   * rebuildTick] and the ADR §2 layout-key gate is not weakened by handler
   * identity churn (PR #8 review).
   *
   * Resolves against `viewCards`, not `cards` (ADR B2): the keyboard resolver
   * and the drop resolver must see the same heights the renderer does.
   */
  const keyboardMoveImplRef = useRef<(uuid: string, action: KeyboardMoveAction) => void>();
  keyboardMoveImplRef.current = (objectiveUuid: string, action: KeyboardMoveAction) => {
    const resolution = resolveKeyboardMove(viewCards, objectiveUuid, action);
    if (resolution.kind === 'move') {
      setPendingFocus({
        objectiveUuid,
        control:
          action.type === 'column'
            ? 'move-column'
            : action.type === 'up'
              ? 'move-up'
              : 'move-down',
      });
      onMoveCard(objectiveUuid, resolution.column, resolution.rank);
    }
  };
  const handleKeyboardMove = useCallback(
    (objectiveUuid: string, action: KeyboardMoveAction) =>
      keyboardMoveImplRef.current?.(objectiveUuid, action),
    [],
  );

  const editTitleImplRef = useRef(onEditTitle);
  editTitleImplRef.current = onEditTitle;
  const stableOnEditTitle = useCallback(
    (uuid: string, text: string) => editTitleImplRef.current(uuid, text),
    [],
  );
  const addKeyResultImplRef = useRef(onAddKeyResult);
  addKeyResultImplRef.current = onAddKeyResult;
  const stableOnAddKeyResult = useCallback(
    (uuid: string, text: string) => addKeyResultImplRef.current(uuid, text),
    [],
  );
  const editKeyResultImplRef = useRef(onEditKeyResult);
  editKeyResultImplRef.current = onEditKeyResult;
  const stableOnEditKeyResult = useCallback(
    (uuid: string, resultUuid: string, text: string) =>
      editKeyResultImplRef.current(uuid, resultUuid, text),
    [],
  );
  /**
   * The key-result checkbox (ADR.ImplementationPlansUX2 C2b/C6b) — the impl-ref
   * pattern the wiring above already uses, but with the pending-focus record
   * armed conditionally, like `keyboardMoveImplRef` above and
   * `setCollapseImplRef` below: `onToggleKeyResultDelivered` is the ONE write
   * handler on this board whose return value is load-bearing, and the record
   * is written only when it actually dispatched. A no-dispatch toggle (a
   * stale `!canEdit`, a concurrent remote demotion, a legacy row) must not
   * arm a record that steals focus at the next unrelated rebuild.
   *
   * `data-control` is unique **per row** (`toggle-delivered:<resultUuid>`),
   * not shared like every other control here — `restoreFocus` takes the FIRST
   * `[data-control]` match in the card, and a shared name would focus row 1
   * after a row-4 toggle.
   *
   * `fallbackColumn` is the card's OWN column, read off `viewCards` at the
   * moment of the write (C6b): the checkbox row hands this handler only
   * `(objectiveUuid, resultUuid, delivered)`, so the column has to be looked
   * up here rather than carried by the caller. It is what the restore
   * effect's branch 2 falls back to when checking the last undelivered key
   * result on a `hideDone` column hides the card out from under it — the
   * one control that explains where the card went.
   */
  const toggleDeliveredImplRef =
    useRef<(objectiveUuid: string, resultUuid: string, delivered: boolean) => void>();
  toggleDeliveredImplRef.current = (
    objectiveUuid: string,
    resultUuid: string,
    delivered: boolean,
  ) => {
    if (onToggleKeyResultDelivered(objectiveUuid, resultUuid, delivered)) {
      const card = viewCards.find((c) => c.uuid === objectiveUuid);
      setPendingFocus({
        objectiveUuid,
        control: `toggle-delivered:${resultUuid}`,
        fallbackColumn: card?.column,
      });
    }
  };
  const handleToggleDelivered = useCallback(
    (objectiveUuid: string, resultUuid: string, delivered: boolean) =>
      toggleDeliveredImplRef.current?.(objectiveUuid, resultUuid, delivered),
    [],
  );

  const editAdditionalInfoImplRef = useRef(onEditAdditionalInfo);
  editAdditionalInfoImplRef.current = onEditAdditionalInfo;
  const stableOnEditAdditionalInfo = useCallback(
    (uuid: string, text: string) => editAdditionalInfoImplRef.current(uuid, text),
    [],
  );

  const isDraggingRef = useRef(false);
  const draggedIdRef = useRef<string | null>(null);
  const flowWrapperRef = useRef<HTMLDivElement>(null);
  // Focus restoration (WCAG 2.4.3): React relocates a moved card's DOM node
  // during the optimistic rebuild, which blurs the active control to <body>.
  // The initiating control is refocused after the rebuild commits.
  const pendingFocusRef = useRef<PendingFocus | null>(null);
  /**
   * The consecutive-miss counter the restore effect's branches 3/4 bound
   * (ADR.ImplementationPlansUX2 C6b). Reset to 0 every time a record is
   * ARMED (`setPendingFocus` below) — never touched by a miss itself, which
   * only increments it — so a fresh gesture always gets its own two-strike
   * budget regardless of how the previous one ended.
   */
  const pendingFocusMissesRef = useRef(0);
  /**
   * The single place a pending-focus record is armed (ADR.ImplementationPlansUX2
   * C6b): every call site that used to write `pendingFocusRef.current = {...}`
   * directly now goes through here, so "reset the attempt counter whenever the
   * record is assigned" cannot be forgotten at a new call site.
   */
  const setPendingFocus = useCallback((record: PendingFocus) => {
    pendingFocusRef.current = record;
    pendingFocusMissesRef.current = 0;
  }, []);

  /**
   * Sets a card's collapse state explicitly (ADR B4/B4a/B11) — never a bare
   * toggle, so re-asserting the current state is a genuine no-op: no `Set`
   * mutation, no `collapseKey` change (B3), no rebuild, and no pending-focus
   * record — focus simply stays where it is. The impl ref closes over the
   * latest `collapsed`, the same pattern `keyboardMoveImplRef` uses to close
   * over the latest `viewCards` without widening the rebuild effect's deps.
   *
   * Dispatches nothing to any write handler (INV-17): this mutates only the
   * canvas's own session-only `collapsed` state, never `onMoveCard` or any
   * of the edit handlers.
   */
  const setCollapseImplRef = useRef<(objectiveUuid: string, collapse: PlanCardCollapse) => void>();
  setCollapseImplRef.current = (objectiveUuid: string, collapse: PlanCardCollapse) => {
    const isCurrentlyCollapsed = collapsed.has(objectiveUuid);
    const alreadyInTargetState =
      collapse === 'titleOnly' ? isCurrentlyCollapsed : !isCurrentlyCollapsed;
    if (alreadyInTargetState) return;
    // Focus returns to the button the user pressed, not its partner (B11):
    // both buttons are permanently mounted in both renders (B4), so the one
    // that was activated still exists after the transition.
    setPendingFocus({
      objectiveUuid,
      control: collapse === 'titleOnly' ? 'collapse-card' : 'expand-card',
    });
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (collapse === 'titleOnly') next.add(objectiveUuid);
      else next.delete(objectiveUuid);
      return next;
    });
  };
  const handleSetCollapse = useCallback(
    (objectiveUuid: string, collapse: PlanCardCollapse) =>
      setCollapseImplRef.current?.(objectiveUuid, collapse),
    [],
  );

  /**
   * Column set operations (ADR B6): expand-all removes every card in the
   * column from the collapsed set, collapse-all adds every one of them. Both
   * close over the latest `layout` (the impl-ref pattern `keyboardMoveImplRef`
   * and `setCollapseImplRef` already use), so the rebuild effect's deps stay
   * `[layoutKey, collapseKeyValue, hideDoneKeyValue, canEdit, rebuildTick]` —
   * handler identity churn never widens it.
   *
   * **Read `layout.visibleColumnCards`, not `layout.columnCards`**
   * (ADR.ImplementationPlansUX2 C5e) — the exact set the user can see, so a
   * bulk button never silently collapses/expands a card it is currently
   * hiding while the live region announces a smaller number: `cardCount` in
   * the node data below reads the SAME array, so the count the announcement
   * states and the cards the click actually touches can never disagree.
   *
   * Each bails to the SAME `Set` reference when no member actually changes —
   * not merely a "same members" copy — which is what makes an empty column's
   * click a genuine no-op (B6: "neither button is ever disabled") and not
   * just a same-value one: `setCollapsed(prev)` with an unchanged reference
   * skips the re-render entirely, exactly like a card's own no-op activation
   * (B3's sorted `collapseKey`).
   *
   * Neither dispatches anything to a write handler (INV-17): both mutate only
   * the canvas's own session-only `collapsed` state.
   */
  const expandAllImplRef = useRef<(columnId: ImplementationPlanColumn) => void>();
  expandAllImplRef.current = (columnId: ImplementationPlanColumn) => {
    const uuids = layout.visibleColumnCards[columnId].map((c) => c.uuid);
    setCollapsed((prev) => {
      let next: Set<string> | null = null;
      for (const uuid of uuids) {
        if (prev.has(uuid)) {
          if (!next) next = new Set(prev);
          next.delete(uuid);
        }
      }
      return next ?? prev;
    });
  };
  const handleExpandAll = useCallback(
    (columnId: ImplementationPlanColumn) => expandAllImplRef.current?.(columnId),
    [],
  );

  const collapseAllImplRef = useRef<(columnId: ImplementationPlanColumn) => void>();
  collapseAllImplRef.current = (columnId: ImplementationPlanColumn) => {
    const uuids = layout.visibleColumnCards[columnId].map((c) => c.uuid);
    setCollapsed((prev) => {
      let next: Set<string> | null = null;
      for (const uuid of uuids) {
        if (!prev.has(uuid)) {
          if (!next) next = new Set(prev);
          next.add(uuid);
        }
      }
      return next ?? prev;
    });
  };
  const handleCollapseAll = useCallback(
    (columnId: ImplementationPlanColumn) => collapseAllImplRef.current?.(columnId),
    [],
  );

  /**
   * The hide-done toggle's setter (ADR.ImplementationPlansUX2 C4a/C4b/C4c):
   * flips exactly one column's boolean, never the whole record — per-column
   * scope, the same shape as `expandAllImplRef`/`collapseAllImplRef` acting
   * on one column at a time. `setHideDone`'s identity is already stable
   * (React guarantees a `useState` setter never changes), so — unlike the
   * handlers above — this needs no impl-ref indirection to keep the rebuild
   * effect's deps from widening; it reads no other render-scoped value.
   *
   * Dispatches nothing to any write handler (INV-17/INV-18a): this mutates
   * only the canvas's own session-only `hideDone` state.
   */
  const handleToggleHideDone = useCallback((columnId: ImplementationPlanColumn) => {
    setHideDone((prev) => ({ ...prev, [columnId]: !prev[columnId] }));
  }, []);

  // Rebuild local node state only when the layout key changes (ADR §2), and
  // never mid-drag — a remote update cannot yank a card out from under the
  // pointer; it is applied on the next drop via rebuildTick.
  useEffect(() => {
    if (isDraggingRef.current) {
      // A drag aborted without a dragging:false change (the dragged objective
      // was deleted mid-drag by a collaborator — ReactFlow's XYDrag aborts and
      // emits nothing) must not suppress reconciliation forever: if the dragged
      // card no longer exists, clear the suppression and rebuild now.
      const draggedStillExists =
        draggedIdRef.current !== null &&
        cards.some((c) => c.uuid === draggedIdRef.current);
      if (draggedStillExists) {
        pendingRebuildRef.current = true;
        return;
      }
      // The escape hatch also lifts C6c's freeze, or it outlives the only
      // thing that would lift it: `handleDropSettled` never runs for a drag
      // that emitted no `dragging: false` change, so the board would stay
      // pinned to a snapshot forever. The tick bump is what brings the TRUE
      // projection back — `viewCards` recomputes with `frozenHidden`
      // undefined; this pass still builds nodes from the frozen layout, and
      // the bumped pass replaces them.
      const wasFrozen = hiddenFreezeRef.current !== null;
      isDraggingRef.current = false;
      draggedIdRef.current = null;
      hiddenFreezeRef.current = null;
      if (wasFrozen) setRebuildTick((t) => t + 1);
    }
    // Interleaved node order (ADR B6b): [col-now, ...nowCards, col-next,
    // ...nextCards, col-someday, ...somedayCards] — each column's header
    // controls immediately precede that column's own cards in the DOM and in
    // the tab order, so reaching a column's Expand-all from one of its
    // collapsed cards is bounded by the cards ABOVE it in that column, not by
    // every card on the board (README INV-10's amended second clause).
    //
    // Safe by construction, not by luck (ADR B6b, re-verified against
    // @xyflow/react@12.7.1 / @xyflow/system@0.0.63 before adopting):
    // - Static geometry never overlaps — every card's x-span sits strictly
    //   inside its own column's x-span, and columns are separated by
    //   COLUMN_GAP, so no column's background can paint over another
    //   column's cards regardless of array order.
    // - A dragged card is elevated to z = 1000 by ReactFlow's own
    //   `elevateNodesOnSelect` default (XYDrag selects on drag start), while
    //   every column background stays at z = 0 — mid-drag overlap is handled
    //   by the store's z-index, not by node-array order.
    // `layout.cards` is already grouped column-by-column, then by rank
    // (computeLayout), so filtering by column id here preserves the existing
    // card-to-card order exactly — every shipped `.react-flow__node-stickyNote`
    // locator and `.first()` still resolves to the same element (test 29).
    const nodes: Node[] = [];
    layout.columns.forEach((col) => {
      nodes.push({
        id: `column-${col.column.id}`,
        type: 'planColumn',
        position: { x: col.x, y: col.y },
        data: {
          columnId: col.column.id,
          label: col.column.label,
          width: col.width,
          height: col.height,
          // Read synchronously from the layout the click is about to act on
          // (ADR B6), over the VISIBLE axis (ADR.ImplementationPlansUX2 C5e)
          // — every card the user can currently see in the column, regardless
          // of its own collapse state, since both buttons act on the whole
          // (visible) column.
          cardCount: layout.visibleColumnCards[col.column.id].length,
          onExpandAll: handleExpandAll,
          onCollapseAll: handleCollapseAll,
          // The hide-done toggle (C4a-C4c): this column's own boolean, never
          // the whole record. `doneCount` is deliberately the FULL order
          // (`columnCards`, not `visibleColumnCards`) — C5e's other half —
          // so the toggle's name and announcement never read zero at the
          // moment the filter it names takes effect.
          hideDone: hideDone[col.column.id],
          doneCount: layout.columnCards[col.column.id].filter(isCardDone).length,
          onToggleHideDone: handleToggleHideDone,
        },
        width: col.width,
        height: col.height,
        draggable: false,
        selectable: false,
        focusable: false,
      });
      layout.cards
        .filter((cp) => cp.card.column === col.column.id)
        .forEach((cp) => {
          nodes.push({
            id: cp.card.uuid,
            type: 'stickyNote',
            position: { x: cp.x, y: cp.y },
            data: {
              card: cp.card,
              width: cp.width,
              height: cp.height,
              canEdit,
              onEditTitle: stableOnEditTitle,
              onAddKeyResult: stableOnAddKeyResult,
              onEditKeyResult: stableOnEditKeyResult,
              onToggleDelivered: handleToggleDelivered,
              onEditAdditionalInfo: stableOnEditAdditionalInfo,
              onKeyboardMove: handleKeyboardMove,
              onSetCollapse: handleSetCollapse,
            },
            width: cp.width,
            height: cp.height,
            draggable: canEdit,
            // The node wrapper is the focusable element, so the accessible
            // name goes here (ReactFlow renders it as aria-label on the
            // wrapper) — an aria-label on an inner div names nothing (PR #5
            // review finding). It carries the STATUS as well (UX2 C1f): the
            // rail and tint are the sighted cues, and this name is what makes
            // the same information non-visual — the ONLY textual route to a
            // card's status since mr-60dv.13 removed the visible glyph. The
            // card root repeats it as a `title` for the pointer. It is also
            // what branch 2b of the restore effect reads out when a toggle
            // click destroys the row the user was standing on (mr-60dv.10).
            ariaLabel: `Objective: ${cp.card.text || 'Untitled objective'}. Status: ${cardStatusLabel(
              cp.card.statusCode,
              cp.card.hasResults,
              cp.card.hasKeyResults,
              cp.card.objectiveIsParent,
            )}.`,
          });
        });
    });

    // Recorded BEFORE setNodes so the restore effect's commit gate (C6b) can
    // compare against the exact reference this pass built, the instant that
    // reference becomes the committed `nodes` state.
    lastBuiltNodesRef.current = nodes;
    setNodes(nodes);
    // layoutKey is the deliberate DATA rebuild trigger; collapseKeyValue and
    // hideDoneKeyValue are the two VIEW ones (ADR B3, UX2 C6a). Neither view
    // gesture changes a Firestore field, so neither changes layoutKey —
    // without these keys the board would recompute heights and never
    // reposition anything. A redundant toggle is a no-op, because a string key
    // that does not change does not re-run the effect. layout/canEdit change
    // with one of the three.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutKey, collapseKeyValue, hideDoneKeyValue, canEdit, rebuildTick]);

  /**
   * Restore focus to the initiating control after the rebuild commits (WCAG
   * 2.4.3 — see utils/focusRestore.ts). **The restore effect gains four
   * branches** (ADR.ImplementationPlansUX2 C6b, tech-lead follow-up ruling
   * on PR #482), plus an attempt counter reset whenever the record is
   * armed. It attempts **at most once per rebuild**: the rebuild effect
   * stores the array it passes to `setNodes` (`lastBuiltNodesRef`), and this
   * effect declines any commit whose `nodes` is a different reference:
   *
   * ```ts
   * if (nodes !== lastBuiltNodesRef.current) return;
   * ```
   *
   * A card-owning click is bundled with ReactFlow's own `'select'` change
   * (with `nodeDragThreshold > 0`, `NodeWrapper`'s `onClick` takes the
   * selection `XYDrag` would otherwise have taken at mousedown, so it
   * batches with the write), and a drag emits one per frame. Without the
   * gate any of those commits can consume a record, and "second
   * **consecutive** miss" counts commits of arbitrary origin rather than
   * rebuilds — so the two-strike budget can be spent before the rebuild the
   * record was armed for ever lands. That, not DOM freshness, is what the
   * gate buys.
   *
   * **The gate does not — and cannot — make the DOM current.** `<ReactFlow>`
   * renders `GraphView` from its internal store and syncs the `nodes` prop
   * into that store from `StoreUpdater`, a sibling rendered **after**
   * `GraphView`, in a **passive effect** (`@xyflow/react@12.7.1`, `:3534`,
   * `:273-295`). A `nodes` change therefore reaches the rendered node DOM
   * one commit **after** the commit that carries it, in every environment —
   * React effect ordering plus a props→store indirection, not a jsdom
   * artifact. Every branch consulting `restoreFocus` is consequently one
   * commit early, which is why branches 2 and 2b below are decided from
   * `viewCards` **before the DOM is consulted at all** (`decideFocusRestore`
   * in utils/focusRestore.ts — the `!cardPresent`/`!rowSurvives` arms return
   * the same outcome regardless of `controlFound`, so this effect probes
   * both possible values of `controlFound` first and only calls the real
   * `restoreFocus` — its one side effect is `.focus()` — when the two probes
   * disagree, i.e. when the outcome genuinely depends on the DOM).
   *
   * The four branches `decideFocusRestore` can return:
   *
   * 1. `restore` — `restoreFocus` succeeds → clear the record and the miss
   *    counter (shipped).
   * 2. `fallback` — the card itself is gone from `viewCards` → focus falls
   *    back to that column's own hide-done toggle (the one control that
   *    explains where the card went) and the record clears regardless of
   *    whether the fallback was found — a record whose target and fallback
   *    are both gone must not survive to a later, unrelated rebuild.
   * 2b. `clear` with `focus: 'card'` — the card survives but the record's
   *     OWN control cannot exist in the render just committed — a
   *     `toggle-delivered:<uuid>` whose row is gone from `card.keyResults`,
   *     or whose card is now `titleOnly` so B5's block exclusivity removed
   *     the whole list — clear the record and the counter and move focus to
   *     **the card's own node wrapper**, in ONE commit. These are C6b's two
   *     named causes, both decidable from `viewCards` alone, so they need no
   *     two-strike wait. The focus target is bead mr-60dv.10's ruling: the
   *     user's own click destroyed the row, the card is still exactly where
   *     they left it, and the wrapper is focusable and already named
   *     `Objective: … Status: …` — strictly better than `<body>`, and it
   *     announces which objective they are standing on.
   * 3. `retry` — the card survives, its record's control COULD exist,
   *    `restoreFocus` fails anyway, first consecutive miss → leave the
   *    record armed (the shipped retry) and count the miss.
   * 4. `clear` with `focus: 'none'` — same as 3, but the SECOND consecutive
   *    miss → clear the record and the counter, and move focus nowhere. The
   *    whole difference from branches 2 and 2b: the card is still on screen,
   *    nothing vanished out from under the user, AND the cause is one nobody
   *    enumerated, on a rebuild they may not have caused — so any focus move
   *    here would be a rebuild reaching in and grabbing focus anyway.
   *
   * The miss counter is per-record (reset by `setPendingFocus` whenever one
   * is armed), so a fresh gesture always gets its own two-strike budget.
   * `decideFocusRestore` is a pure function, exhaustively unit-tested over
   * every input combination (unit 47f in utils/__tests__/focusRestore.test.ts)
   * — branches 3/4's TRIGGERS are asserted there and only there; their
   * wiring into this effect is not unit-provable in any environment (no
   * environment can stage a `nodes`-prop change and a stale DOM read at the
   * same commit on demand) and is not claimed to be — this effect's own
   * correctness rests on the gate (gate buys "at most once per rebuild",
   * argued above) and on E2E 63/65 plus the two shipped INV-11 relocation
   * assertions (`implementation-plans.spec.ts:987-994`, `:1305-1327`).
   */
  useEffect(() => {
    const pending = pendingFocusRef.current;
    const container = flowWrapperRef.current;
    if (!pending || !container) return;
    if (nodes !== lastBuiltNodesRef.current) return; // not the rebuild's DOM — do not decide on it

    const card = viewCards.find((c) => c.uuid === pending.objectiveUuid && !c.hidden);
    const decisionInput = {
      cardPresent: !!card,
      cardCollapse: card?.collapse,
      keyResultUuids: card?.keyResults.map((r) => r.uuid),
      control: pending.control,
      fallbackColumn: pending.fallbackColumn,
      misses: (pendingFocusMissesRef.current === 0 ? 0 : 1) as 0 | 1,
    };
    // Probe both possible DOM facts first (decideFocusRestore is pure — free
    // to call twice) so `restoreFocus` — the one call with a real side
    // effect — is invoked only when the outcome actually depends on it.
    const ifFound = decideFocusRestore({ ...decisionInput, controlFound: true });
    const ifNotFound = decideFocusRestore({ ...decisionInput, controlFound: false });
    const decision =
      ifFound.kind === ifNotFound.kind
        ? ifNotFound
        : decideFocusRestore({ ...decisionInput, controlFound: restoreFocus(container, pending) });

    switch (decision.kind) {
      case 'restore': {
        // Branch 1 (shipped).
        pendingFocusRef.current = null;
        pendingFocusMissesRef.current = 0;
        return;
      }
      case 'fallback': {
        // Branch 2: fall back to the column's own hide-done toggle, when the
        // record carries one. `fallbackColumn` is present only on the
        // `toggle-delivered:*` record; no other gesture's card can
        // disappear out from under it.
        if (decision.column) {
          container
            .querySelector<HTMLElement>(
              `[data-control="toggle-hide-done"][data-column="${decision.column}"]`,
            )
            ?.focus();
        }
        pendingFocusRef.current = null;
        pendingFocusMissesRef.current = 0;
        return;
      }
      case 'retry': {
        // Branch 3: first consecutive miss — leave the record armed.
        pendingFocusMissesRef.current = 1;
        return;
      }
      case 'clear': {
        // Branches 2b and 4. Both clear; only 2b moves focus, and only to the
        // card's OWN node wrapper (bead mr-60dv.10) — the user's toggle click
        // destroyed the row it named while the card stayed put, so there is a
        // focusable, `Objective: … Status: …`-named element exactly where they
        // were standing. Branch 4 (`focus: 'none'`) still moves focus nowhere:
        // its trigger is an unenumerated miss on a rebuild the user may not
        // have caused.
        if (decision.focus === 'card') {
          focusCardNode(container, pending.objectiveUuid);
        }
        pendingFocusRef.current = null;
        pendingFocusMissesRef.current = 0;
        return;
      }
    }
    // `nodes` is the ONLY commit-driving dependency (ADR.ImplementationPlansUX2
    // C6b, tech-lead follow-up ruling on PR #482): this effect must fire
    // exactly once per rebuild-produced commit, decided by the gate above,
    // never by re-running whenever `viewCards`/`layout` alone change (their
    // own recompute always precedes the rebuild effect's own `setNodes` call
    // by at least one commit). `viewCards`, `decideFocusRestore`,
    // `restoreFocus` and the refs are read for their CURRENT value once the
    // gate passes, not to trigger additional runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes]);

  /**
   * The drop settled: release C6c's freeze and replay whatever was deferred.
   *
   * The tick is bumped when a rebuild was suppressed mid-drag (ADR §2) **or**
   * when a freeze was in force — `rebuildTick` is in the `viewCards` memo's
   * deps, so that bump is the whole mechanism by which the true projection
   * returns. Since the capture is unconditional (C6c), the second disjunct is
   * true on every drop, including one whose captured hidden set was empty:
   * that is the priced cost of pinning the empty set, one extra projection
   * recompute batched with the optimistic move.
   *
   * `hiddenFreezeRef` is read immediately before the canvas clears it, which
   * is exactly why this clause is implementable where revision 2's
   * dragged-uuid phrasing was not.
   */
  const handleDropSettled = useCallback(() => {
    const wasFrozen = hiddenFreezeRef.current !== null;
    hiddenFreezeRef.current = null;
    if (pendingRebuildRef.current || wasFrozen) {
      pendingRebuildRef.current = false;
      setRebuildTick((t) => t + 1);
    }
  }, []);

  const onNodesChange = usePlanNodesChangeHandler({
    // viewCards, not cards (ADR B2): the clamp, the drop resolver and the
    // settled-layout snap all read heights from this array, and a collapsed
    // height must never reach the renderer without reaching them. Under the
    // filter it carries `hidden` for the same reason (C5a/C6c).
    cards: viewCards,
    setNodes,
    onMoveCard,
    isDraggingRef,
    draggedIdRef,
    hiddenFreezeRef,
    onDropSettled: handleDropSettled,
  });

  // No role="region" here: the wrapper's <section aria-label="Implementation
  // plans"> is the landmark; duplicating it nested with the same name double-
  // announces to screen readers (PR #5 review finding).
  return (
    <div
      ref={flowWrapperRef}
      style={{ width: layout.canvasWidth, height: layout.canvasHeight }}
    >
      <ReactFlow
        nodes={nodes}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        nodesDraggable={canEdit}
        fitView={false}
        minZoom={1}
        maxZoom={1}
        panOnDrag={false}
        zoomOnScroll={false}
        preventScrolling={false}
        selectionKeyCode={null}
        multiSelectionKeyCode={null}
        selectionOnDrag={false}
        nodesFocusable={true}
        disableKeyboardA11y={true}
        nodeDragThreshold={1}
        translateExtent={[
          [0, 0],
          [layout.canvasWidth, layout.canvasHeight],
        ]}
        style={{ background: 'white' }}
      />
    </div>
  );
}
